import {
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type {
  PaginatedThreadList,
  Thread,
  ThreadActiveRun
} from 'src/api/squad/types.gen'
import type {
  ChatAttachment,
  ChatMessage,
  Conversation,
  ConversationStatus,
  TimelineItem
} from 'src/components/chat'
import { useDebouncedValue } from 'src/hooks'

import {
  enrichApprovalValue,
  parseJsonObject,
  workingStepsToTimeline
} from './chatTimeline'
import { attachmentsOf, withAttachment } from './messageAttachments'
import {
  deleteThread,
  getApproval,
  getThreadMessages,
  listThreads,
  renameThread
} from './squadApi'
import type { ThreadMessages } from './squadApi'

const TEMP_PREFIX = 'temp:'
const NEW_CHAT_TITLE = 'New chat'
const THREAD_HISTORY_PAGE_SIZE = 15
const RUN_POLL_INTERVAL_MS = 2_000
const TITLE_MAX_CHARS = 56
const TITLE_TRUNCATED_CHARS = 53

export const isTempId = (id: string | null): boolean =>
  Boolean(id && id.startsWith(TEMP_PREFIX))

const generateId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const serverMessageId = (
  runId: string | undefined,
  role: string,
  index: number
) => `srv:${runId || index}:${role}`

const isRunInFlight = (run: ThreadActiveRun | null): boolean =>
  run?.status === 'pending' || run?.status === 'running'

const deriveTitle = (firstMessage: string): string => {
  const trimmed = firstMessage.trim().replace(/\s+/g, ' ')
  if (trimmed.length <= TITLE_MAX_CHARS) return trimmed || NEW_CHAT_TITLE

  return `${trimmed.slice(0, TITLE_TRUNCATED_CHARS)}…`
}

const toTimestamp = (iso: string | undefined, fallback: number): number => {
  const t = iso ? Date.parse(iso) : NaN

  return Number.isFinite(t) ? t : fallback
}

const squadThreadsKey = (agentId: string, userSsoId: string, search: string) =>
  ['squad-threads', agentId, userSsoId, search] as const

const squadThreadsKeyPrefix = (agentId: string) =>
  ['squad-threads', agentId] as const

const nextPageNumber = (
  next: string | null | undefined
): number | undefined => {
  if (!next) return undefined
  const match = next.match(/[?&]page=(\d+)/)

  return match ? Number(match[1]) : undefined
}

type Overlay = {
  messages: ChatMessage[]
  title?: string
  createdAt?: number
  updatedAt?: number
}

export type ReconnectRequest = {
  runId: string
  messageId: string
  seed: TimelineItem[]
}

export type UseSquadConversationsApi = {
  conversations: Conversation[]
  activeId: string | null
  active: Conversation | null
  search: string
  setSearch: (value: string) => void
  loadingList: boolean
  loadingMoreList: boolean
  hasMoreConversations: boolean
  loadingActive: boolean
  loadMoreConversations: () => void
  selectConversation: (id: string | null) => void
  startConversation: (
    firstUserMessage: string,
    attachments?: ChatAttachment[]
  ) => Conversation
  appendUserMessage: (
    conversationId: string,
    content: string,
    attachments?: ChatAttachment[]
  ) => ChatMessage
  appendAssistantMessage: (conversationId: string) => ChatMessage
  updateAssistantMessage: (
    conversationId: string,
    messageId: string,
    content: string,
    timeline?: TimelineItem[]
  ) => void
  setAssistantRunId: (
    conversationId: string,
    messageId: string,
    runId: string
  ) => void
  setAssistantLive: (
    conversationId: string,
    messageId: string,
    live: boolean
  ) => void
  addAssistantAttachment: (
    conversationId: string,
    messageId: string,
    attachment: ChatAttachment
  ) => void
  removeMessage: (conversationId: string, messageId: string) => void
  deleteConversation: (conversationId: string) => void
  renameConversation: (conversationId: string, title: string) => void
  stopRunPolling: (conversationId: string) => void
  reconnectRequests: Record<string, ReconnectRequest>
  clearReconnectRequest: (conversationId: string) => void
  startRunPolling: (conversationId: string) => void
  commitThreadId: (tempId: string, threadId: string) => void
  reset: () => void
}

export const useSquadConversations = (
  appToken: string,
  agentId: string | null,
  enabled: boolean,
  userSsoId?: string
): UseSquadConversationsApi => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search)
  const listKey = squadThreadsKey(
    agentId ?? '',
    userSsoId ?? '',
    debouncedSearch
  )

  const listQuery = useInfiniteQuery({
    queryKey: listKey,
    queryFn: ({ pageParam }) =>
      listThreads(
        appToken,
        agentId ?? '',
        Number(pageParam),
        THREAD_HISTORY_PAGE_SIZE,
        debouncedSearch,
        userSsoId
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      nextPageNumber((lastPage as PaginatedThreadList).next),
    enabled: enabled && Boolean(agentId) && Boolean(appToken)
  })

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingList
  } = listQuery

  const [overlay, setOverlay] = useState<Record<string, Overlay>>({})
  const [activeId, setActiveId] = useState<string | null>(null)
  const [loadingActive, setLoadingActive] = useState(false)
  const [pollingThreads, setPollingThreads] = useState<ReadonlySet<string>>(
    new Set()
  )
  const [reconnectRequests, setReconnectRequests] = useState<
    Record<string, ReconnectRequest>
  >({})
  const loadedMessages = useRef<Set<string>>(new Set())

  const invalidateList = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: squadThreadsKeyPrefix(agentId ?? '')
      }),
    [queryClient, agentId]
  )

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteThread(appToken, id),
    onSettled: () => void invalidateList()
  })

  const renameMutation = useMutation({
    mutationFn: (vars: { id: string; title: string }) =>
      renameThread(appToken, vars.id, vars.title),
    onSettled: () => void invalidateList()
  })

  const conversations = useMemo<Conversation[]>(() => {
    const byId = new Map<string, Conversation>()

    const threads = (listQuery.data?.pages ?? [])
      .flatMap((page) => page.results ?? [])
      .reduce<Thread[]>((acc, thread) => {
        if (!acc.some((item) => item.id === thread.id)) acc.push(thread)

        return acc
      }, [])

    for (const thread of threads) {
      const o = overlay[thread.id]
      byId.set(thread.id, {
        id: thread.id,
        title: o?.title ?? thread.title ?? NEW_CHAT_TITLE,
        createdAt: toTimestamp(thread.created_at, 0),
        updatedAt: o?.updatedAt ?? toTimestamp(thread.last_activity_at, 0),
        messages: o?.messages ?? [],
        status: thread.active_run?.status as ConversationStatus | undefined
      })
    }

    for (const [id, o] of Object.entries(overlay)) {
      if (byId.has(id)) continue
      const hiddenBySearch = debouncedSearch && id !== activeId
      if (hiddenBySearch) continue
      byId.set(id, {
        id,
        title: o.title ?? NEW_CHAT_TITLE,
        createdAt: o.createdAt ?? o.updatedAt ?? 0,
        updatedAt: o.updatedAt ?? 0,
        messages: o.messages
      })
    }

    return [...byId.values()].sort((a, b) => b.updatedAt - a.updatedAt)
  }, [listQuery.data, overlay, debouncedSearch, activeId])

  const loadMoreConversations = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const editMessages = useCallback(
    (
      conversationId: string,
      edit: (messages: ChatMessage[]) => ChatMessage[],
      bumpUpdatedAt = true
    ) =>
      setOverlay((prev) => {
        const current = prev[conversationId] ?? { messages: [] }

        return {
          ...prev,
          [conversationId]: {
            ...current,
            messages: edit(current.messages),
            ...(bumpUpdatedAt ? { updatedAt: Date.now() } : {})
          }
        }
      }),
    []
  )

  const applySnapshot = useCallback(
    (
      threadId: string,
      data: ThreadMessages
    ): {
      activeRun: ThreadActiveRun | null
      live: { messageId: string; seed: TimelineItem[] } | null
    } => {
      const now = Date.now()
      const activeRun =
        data.active_run && typeof data.active_run === 'object'
          ? data.active_run
          : null
      const pendingApprovalId =
        activeRun?.status === 'paused'
          ? (activeRun.pending_approval_id ?? null)
          : null
      const liveRunId =
        isRunInFlight(activeRun) && activeRun?.id ? activeRun.id : null

      const messages = data.messages.map((m, index): ChatMessage => {
        const isPausedTurn =
          m.role === 'assistant' &&
          !!pendingApprovalId &&
          (m.status === 'paused' ||
            (!!activeRun?.id && m.run_id === activeRun.id))
        const reconstructedSteps =
          m.role === 'assistant'
            ? workingStepsToTimeline(
                m.working_steps,
                isPausedTurn
                  ? { approvalId: pendingApprovalId as string }
                  : null
              )
            : []
        const stepsCarryAnswer = reconstructedSteps.some(
          (item) => item.kind === 'text'
        )
        const timeline =
          m.role === 'assistant'
            ? [
                ...reconstructedSteps,
                ...(m.text && !stepsCarryAnswer
                  ? [{ kind: 'text' as const, text: m.text }]
                  : [])
              ]
            : []
        const live =
          m.role === 'assistant' && !!liveRunId && m.run_id === liveRunId

        return {
          id: serverMessageId(m.run_id, m.role, index),
          role: m.role,
          content: m.text,
          createdAt: toTimestamp(m.created_at, now),
          ...(timeline.length ? { timeline } : {}),
          ...attachmentsOf(m.files),
          ...(m.run_id ? { runId: m.run_id } : {}),
          ...(live ? { live: true } : {})
        }
      })

      if (liveRunId && !messages.some((m) => m.live)) {
        messages.push({
          id: serverMessageId(liveRunId, 'assistant', messages.length),
          role: 'assistant',
          content: '',
          createdAt: now,
          runId: liveRunId,
          live: true
        })
      }

      setOverlay((prev) => ({
        ...prev,
        [threadId]: { ...(prev[threadId] ?? {}), messages }
      }))

      const liveMessage = messages.find((m) => m.live)
      const live = liveMessage
        ? { messageId: liveMessage.id, seed: liveMessage.timeline ?? [] }
        : null

      return { activeRun, live }
    },
    []
  )

  const enrichApproval = useCallback(
    (threadId: string, approvalId: string) => {
      getApproval(appToken, approvalId)
        .then((approval) => {
          const patch = parseJsonObject(approval.payload) ?? {}
          setOverlay((prev) => {
            const current = prev[threadId]
            if (!current) return prev

            return {
              ...prev,
              [threadId]: {
                ...current,
                messages: current.messages.map((m) =>
                  m.timeline
                    ? {
                        ...m,
                        timeline: enrichApprovalValue(
                          m.timeline,
                          approvalId,
                          patch
                        )
                      }
                    : m
                )
              }
            }
          })
        })
        .catch(() => undefined)
    },
    [appToken]
  )

  const removePollingThread = useCallback((threadId: string) => {
    setPollingThreads((prev) => {
      if (!prev.has(threadId)) return prev
      const next = new Set(prev)
      next.delete(threadId)

      return next
    })
  }, [])

  const loadMessages = useCallback(
    (threadId: string) => {
      if (isTempId(threadId) || loadedMessages.current.has(threadId)) return
      loadedMessages.current.add(threadId)
      setLoadingActive(true)
      queryClient
        .fetchQuery({
          queryKey: ['squad-thread-messages', threadId],
          queryFn: () => getThreadMessages(appToken, threadId)
        })
        .then((data) => {
          const { activeRun, live } = applySnapshot(threadId, data)
          const runId = activeRun?.id
          if (isRunInFlight(activeRun) && runId && live) {
            setReconnectRequests((prev) => ({
              ...prev,
              [threadId]: { runId, messageId: live.messageId, seed: live.seed }
            }))
          } else if (
            activeRun?.status === 'paused' &&
            activeRun.pending_approval_id
          ) {
            enrichApproval(threadId, activeRun.pending_approval_id)
          }
        })
        .catch(() => {
          loadedMessages.current.delete(threadId)
        })
        .finally(() => setLoadingActive(false))
    },
    [appToken, queryClient, applySnapshot, enrichApproval]
  )

  useEffect(() => {
    if (!pollingThreads.size || !appToken) return

    let cancelled = false
    const tick = async () => {
      for (const threadId of pollingThreads) {
        const fresh = await getThreadMessages(appToken, threadId).catch(
          () => null
        )
        if (cancelled) return
        if (!fresh) continue
        const { activeRun } = applySnapshot(threadId, fresh)
        if (isRunInFlight(activeRun)) continue
        removePollingThread(threadId)
        if (activeRun?.status === 'paused' && activeRun.pending_approval_id) {
          enrichApproval(threadId, activeRun.pending_approval_id)
        }
        void invalidateList()
      }
    }
    const interval = setInterval(() => void tick(), RUN_POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [
    pollingThreads,
    appToken,
    applySnapshot,
    enrichApproval,
    removePollingThread,
    invalidateList
  ])

  const stopRunPolling = useCallback(
    (conversationId: string) => {
      if (!pollingThreads.has(conversationId)) return
      removePollingThread(conversationId)
      editMessages(
        conversationId,
        (messages) => messages.map((m) => (m.live ? { ...m, live: false } : m)),
        false
      )
    },
    [pollingThreads, removePollingThread, editMessages]
  )

  const startRunPolling = useCallback((conversationId: string) => {
    setPollingThreads((prev) => {
      if (prev.has(conversationId)) return prev
      const next = new Set(prev)
      next.add(conversationId)

      return next
    })
  }, [])

  const clearReconnectRequest = useCallback((conversationId: string) => {
    setReconnectRequests((prev) => {
      if (!prev[conversationId]) return prev
      const { [conversationId]: _dropped, ...rest } = prev

      return rest
    })
  }, [])

  const reset = useCallback(() => {
    setOverlay({})
    setActiveId(null)
    setLoadingActive(false)
    setPollingThreads(new Set())
    setReconnectRequests({})
    loadedMessages.current = new Set()
  }, [])

  const selectConversation = useCallback(
    (id: string | null) => {
      setActiveId(id)
      if (id) loadMessages(id)
    },
    [loadMessages]
  )

  const startConversation = useCallback(
    (
      firstUserMessage: string,
      attachments?: ChatAttachment[]
    ): Conversation => {
      const now = Date.now()
      const conversation: Conversation = {
        id: `${TEMP_PREFIX}${generateId()}`,
        title: deriveTitle(
          firstUserMessage || attachments?.[0]?.filename || ''
        ),
        createdAt: now,
        updatedAt: now,
        messages: [
          {
            id: generateId(),
            role: 'user',
            content: firstUserMessage,
            createdAt: now,
            ...(attachments?.length ? { attachments } : {})
          }
        ]
      }
      setOverlay((prev) => ({
        ...prev,
        [conversation.id]: {
          messages: conversation.messages,
          title: conversation.title,
          createdAt: now,
          updatedAt: now
        }
      }))
      setActiveId(conversation.id)

      return conversation
    },
    []
  )

  const appendUserMessage = useCallback(
    (
      conversationId: string,
      content: string,
      attachments?: ChatAttachment[]
    ): ChatMessage => {
      const message: ChatMessage = {
        id: generateId(),
        role: 'user',
        content,
        createdAt: Date.now(),
        ...(attachments?.length ? { attachments } : {})
      }
      editMessages(conversationId, (messages) => [...messages, message])

      return message
    },
    [editMessages]
  )

  const appendAssistantMessage = useCallback(
    (conversationId: string): ChatMessage => {
      const message: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: '',
        createdAt: Date.now(),
        live: true
      }
      editMessages(conversationId, (messages) => [...messages, message])

      return message
    },
    [editMessages]
  )

  const updateAssistantMessage = useCallback(
    (
      conversationId: string,
      messageId: string,
      content: string,
      timeline?: TimelineItem[]
    ) => {
      editMessages(conversationId, (messages) =>
        messages.map((m) =>
          m.id === messageId
            ? { ...m, content, ...(timeline ? { timeline } : {}) }
            : m
        )
      )
    },
    [editMessages]
  )

  const setAssistantRunId = useCallback(
    (conversationId: string, messageId: string, runId: string) => {
      if (!runId) return
      editMessages(
        conversationId,
        (messages) =>
          messages.map((m) => (m.id === messageId ? { ...m, runId } : m)),
        false
      )
    },
    [editMessages]
  )

  const setAssistantLive = useCallback(
    (conversationId: string, messageId: string, live: boolean) => {
      editMessages(
        conversationId,
        (messages) =>
          messages.map((m) => (m.id === messageId ? { ...m, live } : m)),
        false
      )
      if (!live) void invalidateList()
    },
    [editMessages, invalidateList]
  )

  const addAssistantAttachment = useCallback(
    (conversationId: string, messageId: string, attachment: ChatAttachment) =>
      editMessages(
        conversationId,
        (messages) => withAttachment(messages, messageId, attachment),
        false
      ),
    [editMessages]
  )

  const removeMessage = useCallback(
    (conversationId: string, messageId: string) => {
      editMessages(conversationId, (messages) =>
        messages.filter((m) => m.id !== messageId)
      )
    },
    [editMessages]
  )

  const commitThreadId = useCallback(
    (tempId: string, threadId: string) => {
      loadedMessages.current.add(threadId)
      setOverlay((prev) => {
        const temp = prev[tempId]
        if (!temp) return prev
        const { [tempId]: _dropped, ...rest } = prev

        return { ...rest, [threadId]: temp }
      })
      setActiveId((current) => (current === tempId ? threadId : current))
      void invalidateList()
    },
    [invalidateList]
  )

  const deleteConversation = useCallback(
    (conversationId: string) => {
      setOverlay((prev) => {
        if (!prev[conversationId]) return prev
        const { [conversationId]: _dropped, ...rest } = prev

        return rest
      })
      queryClient.setQueryData<InfiniteData<PaginatedThreadList>>(
        squadThreadsKey(agentId ?? '', userSsoId ?? '', debouncedSearch),
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  results: page.results.filter((t) => t.id !== conversationId)
                }))
              }
            : old
      )
      setActiveId((current) => (current === conversationId ? null : current))
      removePollingThread(conversationId)
      clearReconnectRequest(conversationId)
      if (!isTempId(conversationId)) deleteMutation.mutate(conversationId)
    },
    [
      queryClient,
      agentId,
      userSsoId,
      debouncedSearch,
      deleteMutation,
      removePollingThread,
      clearReconnectRequest
    ]
  )

  const renameConversation = useCallback(
    (conversationId: string, title: string) => {
      const next = title.trim()
      if (!next) return
      if (isTempId(conversationId)) {
        setOverlay((prev) => ({
          ...prev,
          [conversationId]: {
            ...(prev[conversationId] ?? { messages: [] }),
            title: next
          }
        }))

        return
      }
      queryClient.setQueryData<InfiniteData<PaginatedThreadList>>(
        squadThreadsKey(agentId ?? '', userSsoId ?? '', debouncedSearch),
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) => ({
                  ...page,
                  results: page.results.map((t) =>
                    t.id === conversationId ? { ...t, title: next } : t
                  )
                }))
              }
            : old
      )
      renameMutation.mutate({ id: conversationId, title: next })
    },
    [queryClient, agentId, userSsoId, debouncedSearch, renameMutation]
  )

  const active = conversations.find((c) => c.id === activeId) ?? null

  return useMemo(
    () => ({
      conversations,
      activeId,
      active,
      search,
      setSearch,
      loadingList: isLoadingList,
      loadingMoreList: isFetchingNextPage,
      hasMoreConversations: Boolean(hasNextPage),
      loadingActive,
      loadMoreConversations,
      selectConversation,
      startConversation,
      appendUserMessage,
      appendAssistantMessage,
      updateAssistantMessage,
      setAssistantRunId,
      setAssistantLive,
      addAssistantAttachment,
      removeMessage,
      deleteConversation,
      renameConversation,
      commitThreadId,
      stopRunPolling,
      reconnectRequests,
      clearReconnectRequest,
      startRunPolling,
      reset
    }),
    [
      conversations,
      activeId,
      active,
      search,
      isLoadingList,
      isFetchingNextPage,
      hasNextPage,
      loadingActive,
      loadMoreConversations,
      selectConversation,
      startConversation,
      appendUserMessage,
      appendAssistantMessage,
      updateAssistantMessage,
      setAssistantRunId,
      setAssistantLive,
      addAssistantAttachment,
      removeMessage,
      deleteConversation,
      renameConversation,
      commitThreadId,
      stopRunPolling,
      reconnectRequests,
      clearReconnectRequest,
      startRunPolling,
      reset
    ]
  )
}
